import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Clock, TrendingUp, Calendar, RefreshCw, AlertCircle } from 'lucide-react';
import { Spinner, Button } from '@tendedloop/ui';
import { StatCard } from '@/components/ui/StatCard';
import { useTaskStore } from '@/stores/taskStore';

function formatTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export function Stats() {
  const { t } = useTranslation(['operator', 'common']);
  const {
    tasks,
    stats,
    isLoading,
    error,
    fetchTasks,
    fetchStats,
    clearError,
  } = useTaskStore();

  // Fetch stats and completed tasks on mount
  useEffect(() => {
    fetchStats();
    fetchTasks('COMPLETED');
  }, [fetchStats, fetchTasks]);

  const handleRefresh = () => {
    clearError();
    fetchStats();
    fetchTasks('COMPLETED');
  };

  // Get recent completed tasks
  const recentCompletions = tasks
    .filter(t => t.status === 'COMPLETED')
    .slice(0, 5);

  if (isLoading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] px-6 text-center">
        <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="h-8 w-8 text-error" />
        </div>
        <h2 className="text-xl font-semibold text-neutral-dark mb-2">
          {t('operator:stats.failedToLoad')}
        </h2>
        <p className="text-neutral-medium mb-6">{error}</p>
        <Button
          variant="outline"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          onClick={handleRefresh}
        >
          {t('common:buttons.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-neutral-dark">
          {t('operator:stats.title')}
        </h1>
        <button
          onClick={handleRefresh}
          className="p-2 text-neutral-medium hover:text-neutral-dark hover:bg-neutral-light rounded-lg transition-colors"
        >
          <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <StatCard
          label={t('operator:stats.completedToday')}
          value={stats.completedToday}
          icon={<CheckCircle className="h-5 w-5" />}
          variant="success"
        />
        <StatCard
          label={t('operator:stats.newTasks')}
          value={stats.new}
          icon={<Clock className="h-5 w-5" />}
        />
        <StatCard
          label={t('operator:stats.inProgress')}
          value={stats.inProgress}
          icon={<Calendar className="h-5 w-5" />}
        />
        <StatCard
          label={t('operator:stats.totalActive')}
          value={stats.new + stats.inProgress}
          icon={<TrendingUp className="h-5 w-5" />}
        />
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-neutral-light rounded-xl p-4">
        <h2 className="text-lg font-semibold text-neutral-dark mb-4">
          {t('operator:stats.recentCompletions')}
        </h2>
        {recentCompletions.length > 0 ? (
          <div className="space-y-3">
            {recentCompletions.map(task => (
              <div
                key={task.id}
                className="flex items-center justify-between py-2 border-b border-neutral-light last:border-0"
              >
                <div>
                  <p className="font-medium text-neutral-dark">
                    {task.amenity.name}
                  </p>
                  <p className="text-sm text-neutral-medium">
                    {task.amenity.building.name}
                  </p>
                </div>
                <div className="text-right">
                  {task.completedAt && (
                    <p className="text-xs text-neutral-medium">
                      {formatTime(task.completedAt)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-neutral-medium text-center py-4">
            {t('operator:stats.noRecentCompletions')}
          </p>
        )}
      </div>
    </div>
  );
}
